import type { LoadStatus } from '@breaker/shared-types';

/**
 * Dispatcher-side mirror of the API's PAPERWORK machine (S79 split).
 * Canonical table: apps/api/src/modules/loads/state.ts — this copy powers
 * optimistic UI only; the server always re-validates. Two-place edits are a
 * known cost; if it bites a third time, extract to a shared package.
 *
 * Forward path: draft -> confirmed -> assigned -> completed -> invoiced.
 * Step-backs: confirmed -> draft, assigned -> confirmed.
 * cancelled from any pre-terminal state; failed only from assigned.
 * Motion is NOT here — it is a projection of the stops, owned by the
 * server, rendered from `load.motion`.
 *
 * Mark-X (S80): the STATUS card renders ONE contextual forward action from
 * forwardAction(), a quiet step-back from stepBack(), red words for
 * fail/cancel. Entering `assigned` is NEVER a forward button — assignment
 * belongs to the Assign card behind the readyToAssign gate, which is why
 * FORWARD has no entry for `confirmed`.
 */
const ALLOWED: Record<LoadStatus, LoadStatus[]> = {
  // 'requested' (0038): customer-portal booking. Acceptance -> draft/confirmed;
  // rejection via the existing 'cancelled' terminal. No forward/step-back button
  // is wired here — acceptance UI is later work.
  requested: ['draft', 'confirmed', 'cancelled'],
  draft: ['confirmed', 'cancelled'],
  confirmed: ['assigned', 'draft', 'cancelled'],
  assigned: ['completed', 'confirmed', 'cancelled', 'failed'],
  completed: ['invoiced'],
  invoiced: [],
  cancelled: [],
  failed: [],
};

export function nextStatuses(from: LoadStatus): LoadStatus[] {
  return ALLOWED[from] ?? [];
}

export function canTransition(from: LoadStatus, to: LoadStatus): boolean {
  return nextStatuses(from).includes(to);
}

export function isTerminal(status: LoadStatus): boolean {
  return (ALLOWED[status] ?? []).length === 0;
}

export function canCancel(status: LoadStatus): boolean {
  return canTransition(status, 'cancelled');
}

export function canFail(status: LoadStatus): boolean {
  return canTransition(status, 'failed');
}

/** One contextual status action, dispatcher voice. */
export interface StatusAction {
  to: LoadStatus;
  label: string;
}

const FORWARD: Partial<Record<LoadStatus, StatusAction>> = {
  draft: { to: 'confirmed', label: 'Customer confirmed' },
  assigned: { to: 'completed', label: 'Mark completed' },
  completed: { to: 'invoiced', label: 'Mark invoiced' },
};

const STEP_BACK: Partial<Record<LoadStatus, StatusAction>> = {
  confirmed: { to: 'draft', label: 'Back to draft' },
  assigned: { to: 'confirmed', label: 'Back to confirmed' },
};

/** The ONE forward button for the STATUS card (null = no forward button). */
export function forwardAction(from: LoadStatus): StatusAction | null {
  return FORWARD[from] ?? null;
}

/** The quiet step-back link (null = none). */
export function stepBack(from: LoadStatus): StatusAction | null {
  return STEP_BACK[from] ?? null;
}
// EOF-STATE-MACHINE-V2
